import React from "react";
import TrackChangesIcon from '@mui/icons-material/TrackChanges';
import PersonIcon from '@mui/icons-material/Person';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';

export function ViewSummaryTab({ data, legendDefinitions }) {
  return (
    <div className="space-y-6">
      {/* MATCHED DESIGN: Employee and Rater Card Header */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Employee Card */}
        <div className="flex items-center space-x-4 p-5 bg-white border border-gray-200 rounded-xl shadow-sm">
          <div className="p-3 bg-blue-100 rounded-lg">
            <PersonIcon className="text-blue-600" fontSize="medium" />
          </div>
          <div className="flex-1">
            {/* Increased from 10px to xs (12px) */}
            <p className="text-xs font-bold text-gray-400 uppercase tracking-widest">Ratee / Employee</p>
            <h4 className="text-lg font-bold text-gray-600">{data.employeeName}</h4>
            {/* Increased from xs to sm (14px) */}
            <p className="text-sm text-gray-500 font-medium">
              {data.position} <span className="text-gray-300 mx-1">|</span> {data.department}
            </p>
          </div>
        </div>

        {/* Rater Card */}
        <div className="flex items-center space-x-4 p-5 bg-white border border-gray-200 rounded-xl shadow-sm">
          <div className="p-3 bg-orange-100 rounded-lg">
            <AssignmentIndIcon className="text-orange-600" fontSize="medium" />
          </div>
          <div className="flex-1">
            <div className="flex justify-between items-start">
                <p className="text-xs font-bold text-gray-400 uppercase tracking-widest">Appraiser / Rater</p>
                {/* Increased from 9px to 11px */}
                <span className="px-2 py-1 rounded text-[11px] font-black bg-blue-100 text-blue-600 uppercase tracking-tighter">
                    {data.status}
                </span>
            </div>
            <h4 className="text-lg font-bold text-gray-600">{data.appraiserName}</h4>
            <p className="text-sm text-gray-500 font-medium">
              Period: <span className="text-blue-500 font-bold">{data.period}</span>
            </p>
          </div>
        </div>
      </div>

      {/* Configuration Details */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
        <div className="bg-gray-200 px-4 py-3 border-b border-gray-200 flex items-center space-x-2">
            <TrackChangesIcon className="text-gray-400" fontSize="small" />
            {/* Increased from text-sm to text-base (16px) or kept bold sm */}
            <h3 className="text-sm font-bold text-gray-600 uppercase tracking-wider">KPI Configuration Blueprint</h3>
        </div>
        <div className="p-4 space-y-4">
          {data.performanceCategories?.map((cat) => (
            <div key={cat.id} className="border border-gray-100 rounded overflow-hidden">
              <div className="bg-gray-100 px-4 py-2 flex justify-between items-center border-b border-gray-100">
                {/* Increased from 11px to sm (14px) */}
                <span className="text-sm font-bold text-gray-500 uppercase">{cat.description}</span>
                <span className="text-sm font-bold text-sky-500 bg-white px-3 py-0.5 rounded border border-sky-100">{cat.totalPercentage}%</span>
              </div>
              <div className="p-0">
                <table className="min-w-full divide-y divide-gray-50">
                    <tbody className="bg-white divide-y divide-gray-100">
                        {cat.objectives?.map(obj => (
                        <tr key={obj.id} className="hover:bg-gray-50/50 transition-colors">
                            {/* Increased from 11px to sm (14px) */}
                            <td className="px-4 py-3 text-sm font-bold text-gray-500 w-28">{obj.code}</td>
                            <td className="px-2 py-3 text-sm text-gray-400 leading-relaxed">{obj.description}</td>
                            <td className="px-4 py-3 text-sm text-right font-bold text-gray-400 w-20">{obj.percentage}%</td>
                        </tr>
                        ))}
                    </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Legend Definitions */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-5">
        <div className="flex items-center space-x-2 mb-4">
            <div className="w-1.5 h-5 bg-sky-500 rounded-full"></div>
            {/* Increased from 10px to xs (12px) */}
            <label className="text-xs font-bold text-gray-500 uppercase tracking-widest">Rating Legend Definitions</label>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {legendDefinitions.sort((a,b) => a.rating - b.rating).map(def => (
            <div key={def.id} className="p-3 border border-gray-100 rounded-xl text-center bg-gray-50/40">
              {/* Increased rating from text-sm to text-base */}
              <div className="text-base font-black text-sky-600">{def.rating}</div>
              {/* Increased from 9px to 11px */}
              <div className="text-[11px] font-bold text-gray-600 leading-tight mt-1 uppercase">{def.code}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}